import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SeoMeta } from '../components/seo/SeoMeta';
import { generateChineseName } from '../lib/api/nameGenerator';
import { NameSuggestion } from '../types/name';
import { NameCard } from '../components/name-generator/NameCard';
import { NameList } from '../components/name-generator/NameList';
import { ProgressBar } from '../components/ui/ProgressBar';

export function ChineseBoyNames() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !surname.trim()) {
      setError('Both first name and surname are required.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);

    try {
      const response = await generateChineseName(firstName.trim(), surname.trim(), 'boy');
      setSuggestions(response.suggestions);
      setSelectedIndex(0);
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.getElementById('name-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error occurred while generating names');
    } finally {
      setIsLoading(false);
    }
  };

  // Popular boy names data
  const popularBoyNames = [
    { chinese: '浩然', pinyin: 'Hàorán', meaning: 'Vast and upright', popularity: 5 },
    { chinese: '子轩', pinyin: 'Zǐxuān', meaning: 'Noble son', popularity: 5 },
    { chinese: '宇航', pinyin: 'Yǔháng', meaning: 'Cosmic navigator', popularity: 4 },
    { chinese: '睿泽', pinyin: 'Ruìzé', meaning: 'Wise and benevolent', popularity: 4 },
    { chinese: '文博', pinyin: 'Wénbó', meaning: 'Cultured and learned', popularity: 4 },
    { chinese: '智渊', pinyin: 'Zhìyuān', meaning: 'Wise and profound', popularity: 4 },
    { chinese: '书豪', pinyin: 'Shūháo', meaning: 'Scholarly and outstanding', popularity: 4 },
    { chinese: '刚毅', pinyin: 'Gāngyì', meaning: 'Strong and resolute', popularity: 4 },
    { chinese: '威龙', pinyin: 'Wēilóng', meaning: 'Mighty dragon', popularity: 4 },
    { chinese: '豪杰', pinyin: 'Háojié', meaning: 'Heroic and outstanding', popularity: 4 },
  ];

  const traditionalBoyNames = [
    { chinese: '文博', pinyin: 'Wénbó', meaning: 'Cultured and learned' },
    { chinese: '智渊', pinyin: 'Zhìyuān', meaning: 'Wise and profound' },
    { chinese: '书豪', pinyin: 'Shūháo', meaning: 'Scholarly and outstanding' },
    { chinese: '刚毅', pinyin: 'Gāngyì', meaning: 'Strong and resolute' },
    { chinese: '威龙', pinyin: 'Wēilóng', meaning: 'Mighty dragon' },
    { chinese: '豪杰', pinyin: 'Háojié', meaning: 'Heroic and outstanding' },
    { chinese: '仁义', pinyin: 'Rényì', meaning: 'Benevolent and righteous' },
    { chinese: '正直', pinyin: 'Zhèngzhí', meaning: 'Upright and honest' },
    { chinese: '诚信', pinyin: 'Chéngxìn', meaning: 'Sincere and trustworthy' },
  ];

  const modernBoyNames = [
    { chinese: '星宇', pinyin: 'Xīngyǔ', meaning: 'Starry universe' },
    { chinese: '晨曦', pinyin: 'Chénxī', meaning: 'Morning light' },
    { chinese: '梓涵', pinyin: 'Zǐhán', meaning: 'Catalpa tree and inclusive' },
    { chinese: '天佑', pinyin: 'Tiānyòu', meaning: 'Blessed by heaven' },
    { chinese: '明轩', pinyin: 'Míngxuān', meaning: 'Bright pavilion' },
  ];

  return (
    <>
      <SeoMeta
        title="200+ Chinese Boy Names and Meanings | Chinese-Name.net"
        description="200+ Chinese boy names with meanings & pinyin. Find traditional & modern male names conveying strength & wisdom. Free AI generator helps choose perfect name."
        keywords="chinese boy names with meaning, chinese baby boy names, chinese male names, strong chinese boy names, modern chinese boy names, traditional chinese boy names, unique chinese boy names, popular chinese boy names"
        pathname="/chinese-boy-names"
      />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900">Chinese Boy Names</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl text-center mb-6">
            Chinese Boy Names: 200+ Authentic Names with Meanings
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-8">
            Discover the perfect Chinese name for your baby boy. This comprehensive guide features over 200 authentic Chinese boy names, from traditional classics to modern favorites. Each name includes its Chinese characters, pinyin pronunciation, and cultural meaning.
          </p>

          {/* Quick CTA */}
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto mb-8">
            <p className="text-center text-gray-700 mb-4">
              💡 Want a personalized boy name?
            </p>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Boy Name →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>
        </div>
      </div>

      {/* Introduce Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Are Chinese Boy Names?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Chinese boy names are carefully chosen to reflect positive qualities, virtues, and aspirations. Each name combines Chinese characters (汉字) with specific meanings, creating names that convey strength, wisdom, virtue, and cultural significance. Whether traditional or modern, Chinese boy names carry deep meaning and are an important part of Chinese culture.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose a Chinese Boy Name?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Choosing a Chinese name for your son connects him to rich cultural traditions and meaningful symbolism.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">💪</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Strength & Virtue</h3>
              <p className="text-gray-600 text-sm">Chinese boy names often reflect strength, courage, and moral character.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">📚</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Cultural Heritage</h3>
              <p className="text-gray-600 text-sm">Connect your son to thousands of years of Chinese culture and tradition.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">✨</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Meaningful Identity</h3>
              <p className="text-gray-600 text-sm">Each name carries deep meaning and positive aspirations for your child's future.</p>
            </div>
        </div>
      </div>
      </section>

      {/* Male Chinese names Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Male Chinese names
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our male Chinese names section focuses on strong virtues, thoughtful meanings, and modern flair so you can find the perfect Chinese man’s name.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Male Chinese given names</h3>
              <p className="text-gray-600 mb-4">
                Discover male Chinese given names that bring together character meaning, tone balance, and family heritage. Each suggestion is tagged with Chinese names male and Chinese male first names so you know why it works.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Male Chinese names, Male Chinese given names, Chinese male first names
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Chinese names for boys</h3>
              <p className="text-gray-600 mb-4">
                Filter names by strength, wisdom, or gentleness and compare how Chinese names for boys feel beside your surname. Each entry explains the Chinese boys name meanings in plain English.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese names for boys, Chinese boys name meanings, Chinese names male
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Male Chinese name generator</h3>
              <p className="text-gray-600 mb-4">
                Use our male Chinese name generator when you need strength, leadership, or poetic grit in a Chinese man’s name. It combines modern taste with traditional structure so you get unique, list-worthy names.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Male Chinese name generator, Chinese man’s name
              </p>
            </div>
          </div>
          <p className="text-gray-600 text-center mt-8 max-w-3xl mx-auto">
            This male-focused area supports Chinese name translation by showing names in Chinese and meanings alongside how the names read when you turn Chinese names into English.
          </p>
        </div>
      </section>

      {/* Generated Names Results */}
      {suggestions.length > 0 && (
        <div id="name-results" className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Your Generated Chinese Boy Names
            </h2>
            <NameList
              suggestions={suggestions}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
            />
          </div>
        </div>
      )}

      {/* Most Popular Names */}
      <section id="popular" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Popular Chinese Boy Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            These are the most popular Chinese boy names in 2025, chosen by parents across China and Chinese communities worldwide. Each name carries positive meanings and is easy to pronounce.
          </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chinese</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pinyin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meaning</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popularity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {popularBoyNames.map((name, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-lg font-semibold text-gray-900">{name.chinese}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{name.pinyin}</td>
                    <td className="px-6 py-4 text-gray-600">{name.meaning}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {'⭐'.repeat(name.popularity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Strong CTA Module */}
      <div className="bg-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">🎯 Generate Your Perfect Chinese Boy Name</h3>
            <p className="text-lg text-gray-600 mb-6">Our AI creates personalized names based on your preferences</p>
            <Link
              to="/chinese-name-generator"
              className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-md hover:bg-indigo-700 transition-colors font-medium text-lg"
            >
              Generate Chinese Boy Name Now →
            </Link>
          </div>
        </div>
      </div>

      {/* Traditional Names */}
      {/* Chinese Boy Names with Meaning */}
      <section id="meanings" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese Boy Names with Meaning</h2>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              Every Chinese boy name carries profound meanings. Explore names with meanings that reflect strength, wisdom, virtue, and positive aspirations.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              Meanings Behind Traditional Chinese Boy Names
            </h3>
            <p className="text-gray-600 mb-8 max-w-3xl mx-auto text-center">
              Traditional Chinese boy names emphasize virtues like strength (力量), ambition (志向), and moral character (品德). These names reflect parents' hopes for their sons to grow into strong, virtuous men.
            </p>
          </div>
        </div>
      </section>

      <section id="traditional" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Traditional Chinese Boy Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Traditional Chinese boy names emphasize virtues like wisdom (智), strength (强), and integrity (正). These timeless names have been used for generations and carry deep cultural significance.
          </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {traditionalBoyNames.map((name, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 mb-1">{name.chinese}</div>
                <div className="text-indigo-600 font-medium mb-2">{name.pinyin}</div>
                <div className="text-gray-600">{name.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modern Names */}
      <section id="modern" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Modern Chinese Boy Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Modern Chinese boy names blend traditional elements with contemporary aesthetics. These names are popular among young Chinese parents today.
          </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modernBoyNames.map((name, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1">{name.chinese}</div>
                <div className="text-indigo-600 font-medium mb-2">{name.pinyin}</div>
                <div className="text-gray-600">{name.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Powerful Names */}
      <div id="powerful" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Powerful Chinese Male Names</h2>
          <p className="text-lg text-gray-600 mb-8">
            These powerful Chinese male names convey strength, leadership, and determination. Perfect for parents seeking strong, masculine names.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">霸天</div>
              <div className="text-indigo-600 font-medium mb-2">Bàtiān</div>
              <div className="text-gray-600">Dominate the heavens</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">龙威</div>
              <div className="text-indigo-600 font-medium mb-2">Lóngwēi</div>
              <div className="text-gray-600">Dragon's might</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">战神</div>
              <div className="text-indigo-600 font-medium mb-2">Zhànshén</div>
              <div className="text-gray-600">God of war</div>
            </div>
          </div>
        </div>
      </div>

      {/* Generate a Chinese Boy Name Online */}
      <div id="generate" className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Generate a Chinese Boy Name Online</h2>
          <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto text-center">
            Use our free Chinese baby boy names generator to create personalized names instantly. Enter your preferences and get authentic Chinese boy names with meanings.
          </p>
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Boy Name →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>
        </div>
      </div>

      {/* How to Choose */}
      <div id="how-to-choose" className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Choose a Chinese Boy Name</h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 1: Consider the Pronunciation</h3>
              <p className="text-gray-600">
                Chinese names should be easy to pronounce and pleasant to hear. Test the name with your surname to ensure it flows well.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 2: Check the Meaning</h3>
              <p className="text-gray-600">
                Every Chinese character carries meaning. Ensure the combination creates a positive, meaningful name that reflects your values.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 3: Think About Cultural Context</h3>
              <p className="text-gray-600">
                Certain names are more appropriate for formal vs casual settings. Consider how the name will be used in different contexts.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 4: Test It Out</h3>
              <p className="text-gray-600">
                Say the name out loud, write it down, and see how it feels. Get feedback from native Chinese speakers if possible.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Related Resources */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Related Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">📚 Related Articles</h3>
              <ul className="space-y-2">
                <li><Link to="/blog/chinese-name-generator-male" className="text-indigo-600 hover:underline">Chinese Boy Names and Meanings - Deep Dive</Link></li>
                <li><Link to="/blog/how-to-choose-chinese-name" className="text-indigo-600 hover:underline">How to Choose a Chinese Name</Link></li>
                <li><Link to="/blog/chinese-names-for-foreigners" className="text-indigo-600 hover:underline">Chinese Names for Foreigners</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">👨‍👩‍👧 More Name Categories</h3>
              <ul className="space-y-2">
                <li><Link to="/chinese-girl-names" className="text-indigo-600 hover:underline">Chinese Girl Names</Link></li>
                <li><Link to="/chinese-last-names" className="text-indigo-600 hover:underline">Chinese Last Names</Link></li>
                <li><Link to="/" className="text-indigo-600 hover:underline">Chinese Name Generator</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About Chinese Boy Names
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What makes a good Chinese boy name?
              </h3>
              <p className="text-gray-600">
                A good Chinese boy name combines positive meanings, easy pronunciation, and cultural significance. Names often reflect virtues like strength, wisdom, and integrity.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How do I choose between traditional and modern Chinese boy names?
              </h3>
              <p className="text-gray-600">
                Traditional names carry deep cultural heritage and timeless meanings, while modern names blend traditional elements with contemporary aesthetics. Choose based on your family's values and preferences.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I use a Chinese boy name if I'm not Chinese?
              </h3>
              <p className="text-gray-600">
                Yes, Chinese names can be used by anyone who appreciates Chinese culture. Many non-Chinese parents choose Chinese names for their sons to honor cultural heritage or create meaningful connections.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How does the male Chinese name generator pick names?
              </h3>
              <p className="text-gray-600">
                The male Chinese name generator weighs virtues, tone, and ease of pronunciation to recommend names that feel strong. We also explain the Chinese name meaning for each pick.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What are the best Chinese boy names with meaning?
              </h3>
              <p className="text-gray-600">
                Our dataset includes modern and traditional Chinese boy names with meaning, so you can choose brave, wise, or elegant options depending on your goal.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I translate my English name and view names in Chinese and meanings?
              </h3>
              <p className="text-gray-600">
                Yes. Use our Chinese name translation guidance to see Chinese names into English comparisons with characters, pinyin, and explanations of the names in Chinese and meanings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">Ready to Find the Perfect Chinese Boy Name?</h3>
          <p className="text-xl text-indigo-100 mb-8">Use our AI-powered generator to create personalized names instantly</p>
          <Link
            to="/chinese-name-generator"
            className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium text-lg"
          >
            Generate Chinese Boy Name Now →
          </Link>
        </div>
      </section>
    </>
  );
}

